import type { Auth, User } from "firebase/auth";
import { useMemo, useRef, useState } from "react";
import * as api from "./api";
import { go, useTitle } from "./hooks";
import { TabHead } from "./Menu";
import { Skeleton, SkeletonBar } from "./Skeleton";
import { SizeFailure, SizeTable, useSizeAccounts } from "./SizeTable";
import { filterSizes, NO_SAVINGS, NOTE_TITLE, readSizesFilter, savingNote, SIZE_MEANING, SIZE_COLUMNS, sizeRows, sizeSummary, type SizesFilter, writeSizesFilter } from "./sizes";
import { sortRows } from "./sort";
import { SortSelect, useSort } from "./SortHeader";

/** Manage > Containers > Sizes: the container size and the recommended size of each account (design 8.6). */
export function ContainerSizes({
  base,
  org,
  auth,
  user,
  canView,
  settings,
}: {
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
  settings: boolean;
}) {
  const { accounts, prices, failed, load } = useSizeAccounts(user, canView);
  const [filter, setFilterState] = useState<SizesFilter>(() => readSizesFilter(location.search));
  const setFilter = (next: SizesFilter) => {
    setFilterState(next);
    history.replaceState(history.state, "", `${location.pathname}${writeSizesFilter(location.search, next)}${location.hash}`);
  };
  const { differs: onlyDiffers, q: query } = filter;
  const summary = useRef<HTMLElement>(null);
  const [sort, setSort] = useSort("sort", SIZE_COLUMNS);
  useTitle(`Containers · Sizes · ${org.name}`);
  const rows = useMemo(() => sizeRows(accounts ?? []), [accounts]);
  const differs = useMemo(() => rows.filter((row) => row.differs).length, [rows]);
  const shown = useMemo(() => filterSizes(rows, onlyDiffers, query), [rows, onlyDiffers, query]);
  const hasSavings = useMemo(() => rows.some((row) => row.saving !== null), [rows]);
  const sorted = useMemo(() => (sort ? sortRows(shown, SIZE_COLUMNS, sort) : shown), [shown, sort]);
  return (
    <main className="wide" data-testid="sizes-page">
      <TabHead />
      {canView === false ? (
        <p>Your role does not show the accounts.</p>
      ) : (
        <>
          <p className="sub">
            The recommended size holds the oil of one cycle plus the margin.
            {settings && (
              <>
                {" "}
                <a href={`${base}/admin#containers`} onClick={go} data-testid="sizes-settings-link">
                  Change the standard sizes and the margin
                </a>
              </>
            )}
          </p>
          {hasSavings && (
            <p className="sub">
              <a href={`${base}/size-savings`} onClick={go} data-testid="sizes-savings-link">
                See the savings as a treemap
              </a>
            </p>
          )}
          {rows.length > 0 && (
            <form className="filters" role="search" aria-label="Filter the sizes" onSubmit={(e) => e.preventDefault()}>
              <div>
                <label htmlFor="sizes-search">Search</label>
                <input
                  id="sizes-search"
                  type="search"
                  data-testid="sizes-search"
                  placeholder="Account, name or area"
                  value={query}
                  onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                />
              </div>
              <label className="check">
                <input type="checkbox" data-testid="sizes-differs" checked={onlyDiffers} onChange={(e) => setFilter({ ...filter, differs: e.target.checked })} />
                Only a size more than 25% different
              </label>
            </form>
          )}
          <p className="freshness">
            {accounts ? (
              rows.length > 0 && (
                <strong role="status" tabIndex={-1} ref={summary} data-testid="sizes-summary">
                  {sizeSummary(shown.length, rows.length, differs)}
                </strong>
              )
            ) : failed ? (
              ""
            ) : (
              <SkeletonBar width="9rem" />
            )}
          </p>
          {hasSavings && (
            <details className="size-note" data-testid="sizes-note">
              <summary>{NOTE_TITLE}</summary>
              <p className="sub">
                {SIZE_MEANING} {savingNote(prices)}
              </p>
            </details>
          )}
          {accounts && rows.length > 0 && !hasSavings && prices && <p data-testid="sizes-no-savings">{NO_SAVINGS}</p>}
          <SizeFailure failed={failed} auth={auth} onRetry={load} />
          {accounts === undefined && !failed && <Skeleton shape="table" columns={6} rows={8} />}
          {accounts && rows.length === 0 && <p data-testid="sizes-empty">No accounts in service yet.</p>}
          {rows.length > 0 && shown.length === 0 && <p data-testid="sizes-no-match">No accounts match the filters.</p>}
          {sorted.length > 0 && <SortSelect id="sizes-sort" columns={SIZE_COLUMNS} sort={sort} onSort={setSort} fallback="Account order" />}
          {sorted.length > 0 && <SizeTable rows={sorted} base={base} sort={sort} onSort={setSort} />}
          {sorted.length > 0 && (
            <p className="footnote">
              The recommended size is an estimate from the pickups of the account.
            </p>
          )}
        </>
      )}
    </main>
  );
}
